import { Log } from '@common/log-manager';
import { type UxGoodsCardItemForVerticalProductCard } from '@common/product-card';
import {
  formatCategoryList,
  InAppLikeProductPayload,
  useLikeProductService,
  WebLikeProductPayload,
} from '@widgets/like-product';

export interface UseProductCardOptions {
  onUBLImpression: (item: UxGoodsCardItemForVerticalProductCard, index: number) => void;
  onUBLClick: (item: UxGoodsCardItemForVerticalProductCard, index: number) => void;
  onUBLLikeClick: (isLiked: boolean, item: UxGoodsCardItemForVerticalProductCard, index: number) => void;
  getChildUbl: (item: UxGoodsCardItemForVerticalProductCard, index: number) => Omit<Log, 'category'>;
  onImpression?: () => void;
}
export function useProductCardService(options: UseProductCardOptions) {
  const { onUBLClick, onUBLImpression, onUBLLikeClick, getChildUbl, onImpression } = options;

  const { onLikeProduct } = useLikeProductService();

  const handleImpression = (item: UxGoodsCardItemForVerticalProductCard, index: number) => {
    onUBLImpression(item, index);
    if (index === 4) {
      onImpression?.();
    }
  };

  const handleClick = (item: UxGoodsCardItemForVerticalProductCard, index: number) => {
    onUBLClick(item, index);
  };

  const handleLikeClick = (isLiked: boolean, item: UxGoodsCardItemForVerticalProductCard, index: number) => {
    const childUbl = getChildUbl(item, index);

    const inAppLikeProductPayload: InAppLikeProductPayload = {
      like: isLiked,
      browsingType: item.browsing_type,
      shopId: Number(item.shop_id),
      shopProductNo: item.shop_product_no,
      catalogProductId: item.catalog_product_id,
      title: item.title ?? '',
      goodsUrl: item.product_url ?? '',
      imageUrl: item.webp_image_url ?? item.image_url ?? '',
      price: Number(item.final_price),
      discountRate: Number(item.discount_rate),
      isZpay: Boolean(item.zpay),
      isFreeShipping: Boolean(item.free_shipping),
      log: {
        category: isLiked ? 'save_product' : 'remove_saved_product',
        navigation: childUbl.navigation,
        object_section: childUbl?.object_section,
        object_type: childUbl?.object_type,
        object_idx: childUbl.object_idx,
        object_id: item.catalog_product_id,
        data: childUbl?.data,
        passThrough: true,
      },
    };

    const airbridge = {
      brandID: item.shop_id,
      brandName: item.shop_name ?? '',
      productID: item.catalog_product_id ?? '',
      productName: item.title ?? '',
      price: item.final_price,
      categoryID: formatCategoryList(item.managed_category_list, 'category_id'),
      categoryName: formatCategoryList(item.managed_category_list, 'value'),
    };

    const webLikeProductPayload: WebLikeProductPayload = {
      checked: isLiked,
      productData: { catalog_product_id: item.catalog_product_id ?? '', shop_id: item.shop_id ?? '' },
      airbridge,
      sendLog: (isLiked) => onUBLLikeClick(isLiked, item, index),
    };

    onLikeProduct({ inAppLikeProductPayload, webLikeProductPayload });
  };

  return { onImpression: handleImpression, onClick: handleClick, onLikeClick: handleLikeClick };
}
